import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Grid,
    Button,
    TableBody,
    TableContainer,
    Table,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Skeleton,
    ImageList,
    ImageListItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    ThemeProvider,
    IconButton,
    Box,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    getSurveyPdf,
    getSurveyExcludedImages,
    getSurveyEnrichment,
    downloadingPdf,
    changeSurveyStatus,
    getStoredSurveyPdf,
    getPhraseCategories,
} from '../modules/surveydata';
import { useDispatch } from 'react-redux';
import { flatMapDeep, isObject, uniqBy, map } from 'lodash';
import EditIcon from '@mui/icons-material/Edit';
import Spinner from '../common/spinner';
import PropTypes from 'prop-types';
import Add from '@mui/icons-material/Add';
import AttachmentIcon from '@mui/icons-material/Attachment';
import RichTextEditor from '../common/richtexteditor';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Footer from '../main/footer';
import FooterPadding from '../main/footerpadding';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import ZoomableImage from './zoomableimage';
import VideoList from './videolist';
import { useImageUrlCache } from './useImageUrlCache';
import ConfirmationDialog from '../common/confirmationdialog';
import RefreshIcon from '@mui/icons-material/Refresh';
import PdfOptionDialog from '../common/pdfoptiondialog';
import PdfButton from './pdfbutton';
import StoredPdfDialog from '../common/storedpdfdialog';
import vadoTheme from '../main/VadoThemeMui5';
import SurveyPhraseSummary from './surveyPhraseSummary';

dayjs.extend(utc);

const styles = {
    gridMain: {
        ml: '1em',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
    },
    table: {
        width: '100%',
        mb: '20px',
    },
    paper: {
        p: '16px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
    },
    imageList: {
        width: '100%',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, 300px)',
        justifyContent: 'center',
        gap: '16px',
    },
    imageListItem: {
        maxWidth: 300,
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        bgcolor: 'action.hover',
        boxShadow: 'none',
    },
    image: {
        width: '100%',
        maxWidth: '300px',
        height: 'auto',
    },
    backArrow: {
        cursor: 'pointer',
    },
    imageComment: {
        mt: 1,
        pt: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
    },
    imageMetaLabel: {
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'text.secondary',
        mb: 0.5,
    },
    machineSummaryMeta: {
        color: 'text.secondary',
        fontSize: '0.8rem',
        ml: 1,
    },
};

export default function SurveyView(props) {
    const [surveyEnrichment, setSurveyEnrichment] = useState(null);
    const loadingPdf = useSelector((state) => state.surveydata.downloadingPdf);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [uniqImgIds, setUniqImgIds] = useState([]);
    const [confirmChangeState, setConfirmChangeState] = useState(false);
    const [confirmNextState, setConfirmNextState] = useState('');
    const [excludedImages, setExcludedImages] = useState([]);
    const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
    const [storedPdfDialogOpen, setStoredPdfDialogOpen] = useState(false);
    const [storedPdf, setStoredPdf] = useState(null);
    const [expandedMachineRooms, setExpandedMachineRooms] = useState({});
    const [expandedMachines, setExpandedMachines] = useState({});

    // New state for phrase detection orchestration
    const [phraseCheckStatus, setPhraseCheckStatus] = useState({
        loading: true,
        hasPhrases: false,
        checkedMedia: [],
        shouldUseEnrichment: false,
        phraseCategories: [],
    });

    const onDownloadSurvey = async (download, permanentStore) => {
        setPdfDialogOpen(false);
        // check if user cancelled the action
        if (!download) return;
        dispatch(downloadingPdf());
        await getSurveyPdf(props.survey.id, permanentStore, `${props.survey.name}.pdf`)(dispatch);
    };

    const onDownloadStart = () => {
        setPdfDialogOpen(true);
    };

    const onViewStoredPdf = async () => {
        // reset stored pdf state
        setStoredPdf(null);
        setStoredPdfDialogOpen(true);
        var docs = await getStoredSurveyPdf(props.survey.id)(dispatch);
        setStoredPdf(docs);
    };

    const onCloseStoredPdfDialog = () => {
        setStoredPdfDialogOpen(false);
    };

    const onEditSurvey = () => {
        props.history.push('/survey/edit/' + props.survey.id);
    };

    const onCreateLocation = () => {
        props.history.push('/locations/edit/new?surveyId=' + props.survey.id);
    };

    const onViewLocation = () => {
        props.history.push('/locations/' + props.survey.locationId);
    };

    const onCreateInspection = () => {
        props.history.push('/survey/inspectioncreate/' + props.survey.id);
    };

    const close = () => {
        props.history.goBack();
    };

    // Extract and check machine level media for phrases
    const checkMediaForPhrases = (survey, excludedImageIds) => {
        const results = {
            hasAnyPhrases: false,
            mediaWithPhrases: [],
        };

        // Helper to check if item has phrases
        const hasPhrases = (item) => {
            return item.phrases && item.phrases.length > 0;
        };

        // Helper to process media items
        const processMediaItem = (item, type, machine) => {
            const mediaId = item.imageId || item.videoId;

            // Skip excluded images (only applies to images, not videos)
            if (item.imageId && excludedImageIds.includes(mediaId)) return;

            if (hasPhrases(item)) {
                results.mediaWithPhrases.push({
                    ...item,
                    mediaType: type,
                    machineName: machine.name,
                });
                results.hasAnyPhrases = true;
            }
        };

        // Traverse machine rooms
        if (survey.machineRooms) {
            survey.machineRooms.forEach((machineRoom) => {
                // Machine pictures and videos
                if (machineRoom.machines) {
                    machineRoom.machines.forEach((machine) => {
                        if (machine.pictures) {
                            machine.pictures.forEach((pic) => processMediaItem(pic, 'image', machine));
                        }
                        if (machine.videos) {
                            machine.videos.forEach((video) => processMediaItem(video, 'video', machine));
                        }
                    });
                }
            });
        }

        return results;
    };

    // Orchestrate the data fetching sequence
    useEffect(() => {
        let isMounted = true;

        const orchestrateDataFetching = async () => {
            try {
                // Step 1: Fetch excluded images first (we need this for phrase checking)
                const excludedImageIds = await getSurveyExcludedImages(props.survey.id);
                if (!isMounted) return;

                setExcludedImages(excludedImageIds);

                // Step 2: Check for phrases in non-excluded media
                const phraseResults = checkMediaForPhrases(props.survey, excludedImageIds);

                if (!isMounted) return;

                // Step 3: Decide if we need enrichment data
                const needsEnrichment = !phraseResults.hasAnyPhrases;
                let phraseCategories = [];

                if (phraseResults.hasAnyPhrases) {
                    // Fetch phrase categories since we found phrases
                    phraseCategories = await getPhraseCategories();
                    if (!isMounted) return;
                } else {
                    // Fetch enrichment data instead
                    const enrichment = await getSurveyEnrichment(props.survey.id)(dispatch);

                    if (!isMounted) return;

                    setSurveyEnrichment(enrichment);
                }

                // Step 4: Update final status
                if (!isMounted) return;

                setPhraseCheckStatus({
                    loading: false,
                    hasPhrases: phraseResults.hasAnyPhrases,
                    checkedMedia: phraseResults.mediaWithPhrases,
                    shouldUseEnrichment: needsEnrichment,
                    phraseCategories: phraseCategories,
                });
            } catch (error) {
                console.error('Error orchestrating data fetch:', error);
                if (!isMounted) return;

                // Fallback: use enrichment on error
                setPhraseCheckStatus({
                    loading: false,
                    hasPhrases: false,
                    checkedMedia: [],
                    shouldUseEnrichment: true,
                    phraseCategories: [],
                });
            }
        };

        orchestrateDataFetching();

        return () => {
            isMounted = false;
        };
    }, [props.survey.id, props.survey, dispatch]);

    const getAllSurveyPictures = useCallback((survey) => {
        return flatMapDeep(survey, (value, key) => {
            if (key === 'pictures' && Array.isArray(value)) return value;
            if (key === 'coverPicture' && isObject(value)) return [value];
            if (isObject(value)) return getAllSurveyPictures(value);
            return [];
        });
    }, []);

    useEffect(() => {
        const pictures = getAllSurveyPictures(props.survey);
        setUniqImgIds(uniqBy(map(pictures, 'imageId')));
    }, [props.survey, getAllSurveyPictures]);

    const { imageUrls } = useImageUrlCache(uniqImgIds, props.survey.id, dispatch);

    const getMachineRoomKey = (machineRoom, machineRoomIndex) => {
        return machineRoom && machineRoom.id != null ? `room-${machineRoom.id}` : `room-${machineRoomIndex}`;
    };

    const getMachineKey = (machineRoom, machineRoomIndex, machine, machineIndex) => {
        if (machine && machine.id != null) {
            return `machine-${machine.id}`;
        }
        const roomKey = getMachineRoomKey(machineRoom, machineRoomIndex);
        return `${roomKey}-machine-${machineIndex}`;
    };

    const toggleMachineRoomExpanded = (machineRoomKey) => {
        setExpandedMachineRooms((prev) => ({
            ...prev,
            [machineRoomKey]: !prev[machineRoomKey],
        }));
    };

    const toggleMachineExpanded = (machineKey) => {
        setExpandedMachines((prev) => ({
            ...prev,
            [machineKey]: !prev[machineKey],
        }));
    };

    const expandAllMachineRoomsAndMachines = () => {
        const nextExpandedMachineRooms = {};
        const nextExpandedMachines = {};

        if (props.survey.machineRooms) {
            props.survey.machineRooms.forEach((machineRoom, machineRoomIndex) => {
                const machineRoomKey = getMachineRoomKey(machineRoom, machineRoomIndex);
                nextExpandedMachineRooms[machineRoomKey] = true;

                if (machineRoom.machines) {
                    machineRoom.machines.forEach((machine, machineIndex) => {
                        const machineKey = getMachineKey(machineRoom, machineRoomIndex, machine, machineIndex);
                        nextExpandedMachines[machineKey] = true;
                    });
                }
            });
        }

        setExpandedMachineRooms(nextExpandedMachineRooms);
        setExpandedMachines(nextExpandedMachines);
    };

    const collapseAllMachineRoomsAndMachines = () => {
        setExpandedMachineRooms({});
        setExpandedMachines({});
    };

    useEffect(() => {
        collapseAllMachineRoomsAndMachines();
    }, [props.survey.id]);

    const mapSurveyUsage = (usage, propertyUsageOther) => {
        switch (usage) {
            case 1:
                return t('survey.usageOffice');
            case 2:
                return t('survey.usageProductionIndustrial');
            case 3:
                return t('survey.usageHousing');
            case 4:
                return t('survey.usageWarehouseOrLogistics');
            case 5:
                return t('survey.usageShopping');
            case 6:
                return t('survey.usageSports');
            case 7:
                return propertyUsageOther;
            default:
                return t('survey.undefined');
        }
    };

    const mapClassification = (classification) => {
        switch (classification) {
            case 0:
                return t('survey.undefined');
            case 1:
                return t('survey.classificationOda1');
            case 2:
                return t('survey.classificationOda2');
            case 3:
                return t('survey.classificationOda3');
            default:
                return t('survey.undefined');
        }
    };

    const mapFilterClass = (filterClass) => {
        switch (filterClass) {
            case 1:
                return 'ePM1';
            case 2:
                return 'ePM2,5';
            case 3:
                return 'ePM10';
            case 4:
                return 'Coarse';
            default:
                return t('survey.undefined');
        }
    };

    const mapFilterType = (filterType) => {
        switch (filterType) {
            case 1:
                return t('survey.filterTypeBag');
            case 2:
                return t('survey.filterTypeCartridge');
            case 3:
                return t('survey.filterTypeOther');
            case 4:
                return t('survey.filterTypeCassetteFilter');
            default:
                return t('survey.undefined');
        }
    };

    const mapHeatRecoveryModel = (heatRecoveryModel) => {
        switch (heatRecoveryModel) {
            case 0:
                return t('survey.heatRecoveryNone');
            case 1:
                return t('survey.heatRecoveryRotating');
            case 2:
                return t('survey.heatRecoveryCoil');
            case 3:
                return t('survey.heatRecoveryCounterflow');
            case 4:
                return t('survey.heatRecoveryRotaryWithRecirculation');
            case 5:
                return t('survey.heatRecoveryCoilWithRecirculation');
            default:
                return t('survey.undefined');
        }
    };

    const mapMachineType = (machineType) => {
        switch (machineType) {
            case 1:
                return t('survey.machineTypeSupply');
            case 2:
                return t('survey.machineTypeExhaust');
            default:
                return t('survey.undefined');
        }
    };

    const surveyStatusToText = (status) => {
        switch (status) {
            case 0:
                return t('survey.statusOpen');
            case 1:
                return t('survey.statusCompleted');
            default:
                return '';
        }
    };

    const onCancel = () => {
        props.history.goBack();
    };

    const onMarkReady = () => {
        setConfirmNextState(t('survey.statusCompleted'));
        setConfirmChangeState(true);
    };

    const onMarkOpen = () => {
        setConfirmNextState(t('survey.statusOpen'));
        setConfirmChangeState(true);
    };

    const changeState = async () => {
        await changeSurveyStatus(props.survey.id, props.survey.status === 0 ? 1 : 0)(dispatch);
        props.history.goBack();
    };

    const imageMarkedToPdf = (imageId) => {
        if (excludedImages.includes(imageId)) {
            return false;
        }
        //by default, if imageId is not in exclude list or list empty, return true
        return true;
    };

    const SurveyViewDom = () => {
        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <h2>{t('survey.coverPicture')}</h2>
                    {props.survey.coverPicture ? (
                        <ImageList sx={styles.imageList}>
                            <ImageListItem key={props.survey.coverPicture.imageId} sx={styles.imageListItem}>
                                {!imageUrls[props.survey.coverPicture.imageId] ? (
                                    <SkeletonLoader />
                                ) : (
                                    <ZoomableImage
                                        thumbUrl={imageUrls[props.survey.coverPicture.imageId].thumbUrl}
                                        fullResUrl={imageUrls[props.survey.coverPicture.imageId].fullResUrl}
                                        thumbStyle={styles.image}
                                    />
                                )}
                                {props.survey.coverPicture.comments && (
                                    <Box sx={styles.imageComment}>
                                        <Typography sx={styles.imageMetaLabel}>{t('survey.comments')}</Typography>
                                        <Typography variant="body2">{props.survey.coverPicture.comments}</Typography>
                                    </Box>
                                )}
                            </ImageListItem>
                        </ImageList>
                    ) : (
                        <Typography sx={{ fontFamily: 'italic' }}>{t('survey.coverPictureMissing')}</Typography>
                    )}
                </Grid>
                <Grid item xs={12}>
                    <h2>{t('survey.propertyDetails')}</h2>
                </Grid>
                <Grid item xs={12}>
                    {props.survey.comment}
                </Grid>
                <Grid item xs={12}>
                    <h3>{t('general.address')}</h3>
                    {props.survey.address ? (
                        <React.Fragment>
                            {props.survey.address.name}
                            <br />
                            {props.survey.address.streetAddress},&nbsp;
                            {props.survey.address.zipCode} {props.survey.address.city}
                        </React.Fragment>
                    ) : (
                        <span>{t('survey.noAddressInformation')}</span>
                    )}
                    <br />
                </Grid>
                <Grid item xs={12}>
                    <h2>{t('survey.summary')}</h2>
                    {phraseCheckStatus.loading ? (
                        <Spinner />
                    ) : phraseCheckStatus.shouldUseEnrichment ? (
                        // Show enrichment data when no phrases detected
                        surveyEnrichment ? (
                            <RichTextEditor initialData={surveyEnrichment} viewOnly={true} />
                        ) : (
                            <Spinner />
                        )
                    ) : (
                        // Show phrase-based summary when phrases detected
                        <SurveyPhraseSummary
                            mediaWithPhrases={phraseCheckStatus.checkedMedia}
                            phraseCategories={phraseCheckStatus.phraseCategories}
                        />
                    )}
                    <h3>{t('survey.propertyManagerComments')}</h3>
                    {props.survey.propertyManagerComments}
                </Grid>
                <Grid item xs={12}>
                    {t('survey.usage')}: {mapSurveyUsage(props.survey.usage, props.survey.propertyUsageOther)}
                    <br />
                    {t('survey.classification')}: {mapClassification(props.survey.classification)}
                </Grid>
                {props.survey.videos && props.survey.videos.length > 0 && (
                    <Grid item xs={12}>
                        <h3>{t('survey.videos')}</h3>
                        <VideoList videos={props.survey.videos} />
                    </Grid>
                )}

                {props.survey.machineRooms && (
                    <React.Fragment>
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                                <h2>{t('survey.machineRooms', { pcs: props.survey.machineRooms.length })}</h2>
                                <Box display="flex" gap={1}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={expandAllMachineRoomsAndMachines}>
                                        {t('buttons.expandAll')}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={collapseAllMachineRoomsAndMachines}>
                                        {t('buttons.collapseAll')}
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>

                        {props.survey.machineRooms.map((machineRoom, machineRoomIndex) => (
                            <Grid item xs={12} key={`machineroom_${machineRoomIndex}`}>
                                <Accordion
                                    expanded={!!expandedMachineRooms[getMachineRoomKey(machineRoom, machineRoomIndex)]}
                                    onChange={() =>
                                        toggleMachineRoomExpanded(getMachineRoomKey(machineRoom, machineRoomIndex))
                                    }>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="h6">{machineRoom.name}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {machineRoom.pictures && machineRoom.pictures.length > 0 && (
                                            <h3>{t('survey.images')}</h3>
                                        )}
                                        {machineRoom.pictures && machineRoom.pictures.length > 0 && (
                                            <ImageList sx={styles.imageList}>
                                                {machineRoom.pictures.map(
                                                    (item) =>
                                                        imageMarkedToPdf(item.imageId) && (
                                                            <ImageListItem key={item.imageId} sx={styles.imageListItem}>
                                                                {!imageUrls[item.imageId] ? (
                                                                    <SkeletonLoader />
                                                                ) : (
                                                                    <ZoomableImage
                                                                        thumbUrl={imageUrls[item.imageId].thumbUrl}
                                                                        fullResUrl={imageUrls[item.imageId].fullResUrl}
                                                                        thumbStyle={styles.image}
                                                                    />
                                                                )}
                                                                {item.comments && (
                                                                    <Box sx={styles.imageComment}>
                                                                        <Typography sx={styles.imageMetaLabel}>
                                                                            {t('survey.comments')}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            {item.comments}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </ImageListItem>
                                                        )
                                                )}
                                            </ImageList>
                                        )}
                                        {machineRoom.videos && machineRoom.videos.length > 0 && (
                                            <Grid item xs={12}>
                                                <h3>{t('survey.videos')}</h3>
                                                <VideoList videos={machineRoom.videos} />
                                            </Grid>
                                        )}
                                        {!machineRoom.machines ||
                                            (machineRoom.machines.length === 0 && (
                                                <Grid item xs={12}>
                                                    {t('survey.noMachines')}
                                                </Grid>
                                            ))}
                                        {machineRoom.machines &&
                                            machineRoom.machines.map((machine, machineIndex) =>
                                                (() => {
                                                    const machineKey = getMachineKey(
                                                        machineRoom,
                                                        machineRoomIndex,
                                                        machine,
                                                        machineIndex
                                                    );
                                                    const isMachineExpanded = !!expandedMachines[machineKey];

                                                    return (
                                                        <Accordion
                                                            key={`machineroom_${machineRoomIndex}_machine_${machineIndex}`}
                                                            expanded={isMachineExpanded}
                                                            onChange={() => toggleMachineExpanded(machineKey)}
                                                            sx={{
                                                                mb: 1.5,
                                                                border: '1px solid',
                                                                borderColor: isMachineExpanded
                                                                    ? 'primary.main'
                                                                    : 'divider',
                                                                borderLeft: '3px solid',
                                                                borderLeftColor: 'primary.main',
                                                                borderRadius: 1,
                                                                boxShadow: 'none',
                                                                backgroundColor: 'background.paper',
                                                                '&:before': { display: 'none' },
                                                            }}>
                                                            <AccordionSummary
                                                                expandIcon={
                                                                    <ExpandMoreIcon sx={{ fontSize: '1.1rem' }} />
                                                                }
                                                                sx={{ minHeight: '44px' }}>
                                                                <Box
                                                                    sx={{
                                                                        width: '100%',
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                    }}>
                                                                    <Typography
                                                                        variant="subtitle1"
                                                                        sx={{ fontWeight: 600 }}>
                                                                        {machine.name}
                                                                    </Typography>
                                                                    <Typography sx={styles.machineSummaryMeta}>
                                                                        {mapMachineType(machine.type)}
                                                                    </Typography>
                                                                </Box>
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                {t('survey.machineType')}:{' '}
                                                                {mapMachineType(machine.type)} <br />
                                                                {t('location.airVolume')}: {machine.airVolume} m2
                                                                <br />
                                                                {t('survey.filterClass')}:{' '}
                                                                {mapFilterClass(machine.filterClass)} <br />
                                                                {t('survey.filterEfficiency')}:{' '}
                                                                {machine.filterEfficiency} % <br />
                                                                {t('survey.filterType')}:{' '}
                                                                {mapFilterType(machine.filterType)}
                                                                {machine.filterType === 3 && (
                                                                    <span>&nbsp;({machine.filterTypeOther})</span>
                                                                )}
                                                                <br />
                                                                {machine.type === 2 && (
                                                                    <React.Fragment>
                                                                        {t('survey.heatRecoveryModel')}:{' '}
                                                                        {mapHeatRecoveryModel(
                                                                            machine.heatRecoveryModel
                                                                        )}{' '}
                                                                        <br />
                                                                    </React.Fragment>
                                                                )}
                                                                {t('location.serviceArea')}: {machine.serviceArea}{' '}
                                                                <br />
                                                                {!machine.filters || machine.filters.length === 0 ? (
                                                                    <em>{t('survey.containsNoFilters')}</em>
                                                                ) : (
                                                                    <React.Fragment>
                                                                        <h3>{t('survey.usedFilterDimensions')}</h3>

                                                                        <TableContainer component={Paper}>
                                                                            <Table
                                                                                size="small"
                                                                                aria-label="Filters"
                                                                                sx={{ ...styles.table, maxWidth: 650 }}>
                                                                                <TableHead>
                                                                                    <TableRow>
                                                                                        <TableCell>
                                                                                            {t('survey.width')}
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            {t('survey.height')}
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            {t('survey.maxDepth')}
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            {t('survey.quantity')}
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    {machine.filters.map(
                                                                                        (filter, filterIndex) => (
                                                                                            <TableRow
                                                                                                key={`machineroom_${machineRoomIndex}_machine_${machineIndex}_filter_${filterIndex}`}>
                                                                                                <TableCell>
                                                                                                    {
                                                                                                        filter.dimensionWidth
                                                                                                    }
                                                                                                </TableCell>
                                                                                                <TableCell>
                                                                                                    {
                                                                                                        filter.dimensionHeight
                                                                                                    }
                                                                                                </TableCell>
                                                                                                <TableCell>
                                                                                                    {filter.maxDepth}
                                                                                                </TableCell>
                                                                                                <TableCell>
                                                                                                    {filter.count}
                                                                                                </TableCell>
                                                                                            </TableRow>
                                                                                        )
                                                                                    )}
                                                                                </TableBody>
                                                                            </Table>
                                                                        </TableContainer>
                                                                    </React.Fragment>
                                                                )}
                                                                {machine.numberOfSeals > 0 && (
                                                                    <React.Fragment>
                                                                        <h3>{t('survey.usedSeals')}</h3>

                                                                        <TableContainer component={Paper}>
                                                                            <Table
                                                                                size="small"
                                                                                aria-label="Seals"
                                                                                sx={{ ...styles.table, maxWidth: 650 }}>
                                                                                <TableHead>
                                                                                    <TableRow>
                                                                                        <TableCell>
                                                                                            {t('survey.sealType')}
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            {t('survey.quantity')}
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    <TableRow>
                                                                                        <TableCell>
                                                                                            {machine.selectedSeal}
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            {machine.numberOfSeals}
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                </TableBody>
                                                                            </Table>
                                                                        </TableContainer>
                                                                    </React.Fragment>
                                                                )}
                                                                {machine.pictures && machine.pictures.length > 0 && (
                                                                    <h3>{t('survey.images')}</h3>
                                                                )}
                                                                {machine.pictures && machine.pictures.length > 0 && (
                                                                    <ImageList sx={styles.imageList} cols={3}>
                                                                        {machine.pictures.map((item) => {
                                                                            if (!imageMarkedToPdf(item.imageId)) {
                                                                                return null;
                                                                            }

                                                                            const phrases = Array.isArray(item.phrases)
                                                                                ? item.phrases
                                                                                : Array.isArray(item.filedPhrases)
                                                                                  ? item.filedPhrases
                                                                                  : [];
                                                                            const phraseObservations = phrases
                                                                                .map((phrase) => phrase.observation)
                                                                                .filter(Boolean);
                                                                            const hasObservations =
                                                                                phraseObservations.length > 0;
                                                                            const hasComment = Boolean(item.comments);
                                                                            const shouldShowBar =
                                                                                hasComment || hasObservations;

                                                                            return (
                                                                                <ImageListItem
                                                                                    key={item.imageId}
                                                                                    sx={styles.imageListItem}>
                                                                                    {!imageUrls[item.imageId] ? (
                                                                                        <SkeletonLoader />
                                                                                    ) : (
                                                                                        <ZoomableImage
                                                                                            thumbUrl={
                                                                                                imageUrls[item.imageId]
                                                                                                    .thumbUrl
                                                                                            }
                                                                                            fullResUrl={
                                                                                                imageUrls[item.imageId]
                                                                                                    .fullResUrl
                                                                                            }
                                                                                            thumbStyle={styles.image}
                                                                                        />
                                                                                    )}
                                                                                    {shouldShowBar && (
                                                                                        <Box sx={styles.imageComment}>
                                                                                            {!!item.comments && (
                                                                                                <>
                                                                                                    <Typography
                                                                                                        sx={
                                                                                                            styles.imageMetaLabel
                                                                                                        }>
                                                                                                        {t(
                                                                                                            'survey.comments'
                                                                                                        )}
                                                                                                    </Typography>
                                                                                                    <Typography variant="body2">
                                                                                                        {item.comments}
                                                                                                    </Typography>
                                                                                                </>
                                                                                            )}
                                                                                            {hasObservations && (
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        mt: 0.5,
                                                                                                        display: 'flex',
                                                                                                        alignItems:
                                                                                                            'flex-start',
                                                                                                        gap: 0.5,
                                                                                                    }}>
                                                                                                    <IconButton
                                                                                                        size="small"
                                                                                                        disabled
                                                                                                        sx={{
                                                                                                            color: 'primary.dark',
                                                                                                            padding:
                                                                                                                '2px',
                                                                                                            '&.Mui-disabled':
                                                                                                                {
                                                                                                                    color: 'primary.dark',
                                                                                                                    opacity: 1,
                                                                                                                },
                                                                                                        }}
                                                                                                        aria-label={t(
                                                                                                            'phrases.observation'
                                                                                                        )}>
                                                                                                        <InfoOutlinedIcon fontSize="small" />
                                                                                                    </IconButton>
                                                                                                    <Box>
                                                                                                        {phraseObservations.map(
                                                                                                            (
                                                                                                                observation,
                                                                                                                index
                                                                                                            ) => (
                                                                                                                <Typography
                                                                                                                    key={`${item.imageId}-observation-${index}`}
                                                                                                                    variant="body2"
                                                                                                                    color="primary.dark">
                                                                                                                    •{' '}
                                                                                                                    {
                                                                                                                        observation
                                                                                                                    }
                                                                                                                </Typography>
                                                                                                            )
                                                                                                        )}
                                                                                                    </Box>
                                                                                                </Box>
                                                                                            )}
                                                                                        </Box>
                                                                                    )}
                                                                                </ImageListItem>
                                                                            );
                                                                        })}
                                                                    </ImageList>
                                                                )}
                                                                {machine.videos && machine.videos.length > 0 && (
                                                                    <Grid item xs={12}>
                                                                        <h3>{t('survey.videos')}</h3>
                                                                        <VideoList videos={machine.videos} />
                                                                    </Grid>
                                                                )}
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    );
                                                })()
                                            )}
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>
                        ))}
                    </React.Fragment>
                )}
            </Grid>
        );
    };

    const ActionButtonsDom = () => {
        return (
            <Grid container spacing={2} justifyContent="center" alignItems="center">
                {!!props.survey.locationId && (
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            name="btn-view-location"
                            onClick={onViewLocation}
                            startIcon={<AttachmentIcon />}>
                            {t('general.location')}
                        </Button>
                    </Grid>
                )}
                {!props.survey.locationId && (
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            name="btn-create-location"
                            onClick={onCreateLocation}
                            startIcon={<Add />}>
                            {t('buttons.createLocation')}
                        </Button>
                    </Grid>
                )}
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        name="btn-create-inspection"
                        onClick={onCreateInspection}
                        startIcon={<Add />}>
                        {t('survey.createInspectionReport')}
                    </Button>
                </Grid>
                <Grid item>
                    <PdfButton
                        mainLabel={loadingPdf ? <CircularProgress size={24} color="inherit" /> : 'PDF'}
                        disabled={loadingPdf}
                        onMainClick={onDownloadStart}
                        options={[
                            {
                                label: t('buttons.viewStoredPdf'),
                                onClick: onViewStoredPdf,
                            },
                        ]}
                    />
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        name="btn-survey-edit"
                        onClick={onEditSurvey}
                        startIcon={<EditIcon />}>
                        {t('buttons.edit')}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        name="btn-mark-ready-or-open"
                        onClick={props.survey.status === 0 ? onMarkReady : onMarkOpen}
                        startIcon={props.survey.status === 0 ? <CheckIcon /> : <RefreshIcon />}>
                        {props.survey.status === 0 ? (
                            <span>{t('buttons.markReady')}</span>
                        ) : (
                            <span>{t('survey.statusOpen')}</span>
                        )}
                    </Button>
                </Grid>
                <Grid item>
                    <Button variant="contained" color="secondary" name="btn-close" onClick={close}>
                        {t('buttons.close')}
                    </Button>
                </Grid>
            </Grid>
        );
    };

    //TODO: move to separate component when needed elsewhere
    const SkeletonLoader = ({ width = 150, height = 150 }) => (
        <Skeleton variant="rectangular" width={width} height={height} animation="wave" />
    );

    return (
        <ThemeProvider theme={vadoTheme}>
            <Grid container>
                <Grid container>
                    <Grid item xs={12}>
                        <h2>
                            <ArrowBackIos onClick={onCancel} sx={styles.backArrow} name="close" />
                            <span data-testid="survey-name">
                                {props.survey.name} ({surveyStatusToText(props.survey.status)})
                            </span>
                        </h2>
                    </Grid>
                </Grid>
                <Grid container sx={styles.gridMain} spacing={2}>
                    <Grid item xs={12}>
                        <Paper elevation={1} sx={styles.paper}>
                            <SurveyViewDom />
                        </Paper>
                    </Grid>
                </Grid>
                <ConfirmationDialog
                    useDelay={true}
                    open={confirmChangeState}
                    confirmText={t('buttons.switch')}
                    cancel={() => {
                        setConfirmChangeState(false);
                        setConfirmNextState('');
                    }}
                    confirm={() => {
                        setConfirmChangeState(false);
                        setConfirmNextState('');
                        changeState();
                    }}>
                    {t('survey.confirmChangeSurveyStatus')}
                    &nbsp;<b>{confirmNextState}</b>
                    <br />
                    <br />
                    {t('survey.guidanceOnStatuses')}
                    <ul>
                        <li>
                            <b>{t('survey.statusCompleted')}</b> - {t('survey.guidanceOnStatusesCompleted')}
                        </li>
                        <li>
                            <b>{t('survey.statusOpen')}</b> - {t('survey.guidanceOnStatusesOpen')}
                        </li>
                    </ul>
                </ConfirmationDialog>
                <PdfOptionDialog open={pdfDialogOpen} onClose={onDownloadSurvey} />
                <StoredPdfDialog open={storedPdfDialogOpen} onClose={onCloseStoredPdfDialog} storedPdfs={storedPdf} />
                <Footer>
                    <ActionButtonsDom />
                </Footer>
                <FooterPadding />
            </Grid>
        </ThemeProvider>
    );
}

SurveyView.protoTypes = {
    survey: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
};
